<template>
    <div>
        <q-card class="notification" bordered flat>
            <q-expansion-item
                expand-separator
                v-model="triggerExpansion"
                class="background-red"
                expand-icon-class="text-negative"
                header-class="background-red"
            >
                <template v-slot:header>
                    <div class="row full-width">
                        <q-badge
                            v-if="isNew"
                            class="widget-badge"
                            color="red"
                            :label="$t('New')"
                            floating
                            left
                        />
                        <div v-if="asset" class="text-negative q-ml-xs">
                            <div class="col-md-12">
                                <span class="text-h6 text-bold">
                                    {{ asset.name }}
                                </span>
                            </div>
                            <div class="col-md-4">
                                <span>
                                    {{ receivedAt }} - {{ $t('Downtime') }}
                                </span>
                            </div>
                        </div>
                        <div v-else class="col-md-12">
                            <span class="text-h6">
                                {{
                                    isLoading
                                        ? $t('Loading notification details...')
                                        : $t('Failed to load notification')
                                }}
                            </span>
                            <!-- Reload banner -->
                            <div v-if="showReloadBanner">
                                <q-btn
                                    @click="initialize"
                                    :loading="isLoading"
                                    :label="$t('Reload')"
                                    color="negative"
                                    size="0.8rem"
                                />
                            </div>
                        </div>
                    </div>
                </template>
                <template v-slot:default>
                    <q-card class="notification">
                        <!-- Reload banner -->
                        <q-card-section v-if="showReloadBanner">
                            <q-banner
                                inline-actions
                                class="text-black bg-white"
                            >
                                {{ $t('Failed to load notification details') }}
                                <template v-slot:action>
                                    <q-btn
                                        @click="initialize"
                                        :loading="isLoading"
                                        :label="$t('Reload')"
                                        color="negative"
                                    />
                                </template>
                            </q-banner>
                        </q-card-section>

                        <!-- Body (loading) -->
                        <q-card-section
                            v-if="!asset && isLoading"
                            class="text-center"
                        >
                            <q-spinner
                                color="primary"
                                size="3em"
                                :thickness="10"
                            ></q-spinner>
                            <br />
                            <p class="q-mt-md">
                                {{ $t('Loading notification details...') }}
                            </p>
                        </q-card-section>

                        <!-- Body (loaded) -->
                        <q-card-section v-if="asset" class="q-px-lg q-pt-none">
                            <q-form @submit="submit" v-show="hasCauses">
                                <!-- Body (loaded) -->
                                <planned-shutdown-banner
                                    :show="isPlannedShutdown"
                                    style="padding-top: 2%"
                                />
                                <q-card-section
                                    v-if="proximityReading !== null"
                                    class="row justify-center items-center full-height full-width text-center"
                                >
                                    <div class="vertical-align">
                                        {{ $t('Product in front of sensor :') }}
                                        &nbsp;
                                        <q-icon
                                            v-if="proximityReading == 0"
                                            color="red"
                                            name="fal fa-times"
                                            size="sm"
                                        />
                                        <q-icon
                                            v-if="proximityReading == 1"
                                            color="positive"
                                            name="fas fa-check-square"
                                            size="sm"
                                        />
                                    </div>
                                </q-card-section>
                                <cause-table-select
                                    v-if="causeSelectProps.url"
                                    v-model="form.cause"
                                    @confirmSelection="
                                        form.cause = causeSelectProps.selection
                                    "
                                    :baseURL="causeSelectProps.url"
                                    :queryParams="causeSelectProps.queryParams"
                                    :label="causeSelectProps.label"
                                    :dialogTitle="causeSelectProps.dialogTitle"
                                    :searchLabel="causeSelectProps.searchLabel"
                                    :facilityId="causeSelectProps.facilityId"
                                    :rules="[
                                        val => !!val || $t('Select a cause')
                                    ]"
                                    :dense="true"
                                    :fontSize="'large'"
                                    row-key="id"
                                    class="q-pt-sm notification-header"
                                    @hasItems="
                                        causeSelectProps.hasCauses = $event
                                    "
                                >
                                    <template v-slot:item="data">
                                        <cause-card
                                            @select="
                                                causeSelectProps.selection =
                                                    data.props.row
                                            "
                                            :name="data.props.row.name"
                                            :lossType="
                                                data.props.row.type_of_loss
                                            "
                                            :categoryName="
                                                data.props.row.category_name
                                            "
                                            :planned="data.props.row.planned"
                                            :description="
                                                data.props.row.description
                                            "
                                            :targetDowntime="
                                                data.props.row.target_down_time
                                            "
                                            :isSelected="
                                                causeSelectProps.selection &&
                                                causeSelectProps.selection
                                                    .id === data.props.row.id
                                            "
                                            :isSelectable="true"
                                            :equipmentFailure="
                                                data.props.row.equipment_failure
                                            "
                                        />
                                    </template>
                                </cause-table-select>
                                <q-input
                                    v-model="form.description"
                                    class="q-mt-md"
                                    :label="$t('Description')"
                                    color="accent"
                                    type="textarea"
                                    dense
                                    outlined
                                >
                                    <template v-slot:label>
                                        <div
                                            class="row items-center all-pointer-events"
                                        >
                                            {{ $t('Enter a description') }}
                                        </div>
                                    </template>
                                </q-input>

                                <div
                                    class="q-mt-lg q-mb-md"
                                    :class="{
                                        'flex items-center': $q.screen.gt.xs
                                    }"
                                >
                                    <!-- Add justification images -->
                                    <media-input-manager
                                        v-if="isMediaUploadEnabled"
                                        :mediaCount="justificationMedia.length"
                                        @update:add="
                                            medias =>
                                                justificationMedia.push(
                                                    ...medias
                                                )
                                        "
                                    />
                                    <q-space />
                                    <div>
                                        <q-btn
                                            @click="dismiss"
                                            class="q-mr-md"
                                            color="white"
                                            text-color="secondary"
                                            outline
                                            v-show="allowDismiss"
                                            size="1.2rem"
                                            :class="{
                                                'full-width q-mt-sm':
                                                    $q.screen.xs
                                            }"
                                            :label="$t('Dismiss')"
                                        />
                                        <q-btn
                                            type="submit"
                                            :disable="
                                                $userCannot('create', 'phases')
                                            "
                                            :class="{
                                                'full-width q-mt-sm':
                                                    $q.screen.xs
                                            }"
                                            :loading="isLoading"
                                            color="primary"
                                            size="1.2rem"
                                            outline
                                            :label="$t('Resolve')"
                                        />
                                    </div>
                                </div>
                            </q-form>
                            <div v-if="isMediaUploadEnabled">
                                <q-separator
                                    v-if="justificationMedia.length > 0"
                                    class="q-my-md"
                                />
                                <!-- Display justification images with remove button -->
                                <media-grid-manager
                                    :newMedias="justificationMedia"
                                    @update:removeNewMedia="
                                        newMedias =>
                                            (justificationMedia = newMedias)
                                    "
                                />
                            </div>

                            <div
                                v-if="!hasCauses"
                                class="row q-pa-md text-center"
                            >
                                <div class="q-mt-md text-center full-width">
                                    <q-icon
                                        name="fas fa-exclamation-triangle"
                                        size="md"
                                        class="text-center"
                                        color="secondary"
                                    />
                                </div>
                                <div class="q-my-md text-body1 text-secondary">
                                    {{
                                        $t(
                                            'This asset currently has no causes linked to it. To ensure downtimes can be properly justified, please add causes to this asset.'
                                        )
                                    }}
                                </div>
                                <q-space />
                                <q-btn
                                    @click="dismiss"
                                    color="white"
                                    text-color="secondary"
                                    outline
                                    size="1.2rem"
                                    :class="{
                                        'full-width q-mt-sm': $q.screen.xs
                                    }"
                                >
                                    {{ $t('Dismiss') }}
                                </q-btn>
                            </div>
                        </q-card-section>
                    </q-card>
                </template>
            </q-expansion-item>
        </q-card>
    </div>
</template>

<script>
import CauseCard from '@components/CauseCard.vue';
import CauseTableSelect from '@components/CauseTableSelect.vue';
import PlannedShutdownBanner from '@components/PlannedShutdownBanner.vue';
import { getLocaleFormat, getTime } from '@models/timeformats.js';
import MediaInputManager from '@components/MediaInputManager.vue';
import MediaGridManager from '@components/MediaGridManager.vue';
import { justifyEventWithMedia } from '@utils/mediaManager.js';
import { isFeatureEnabled } from '@utils/featureFlags.js';
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useNotificationsStore } from '@stores/notifications.ts';
import { useAssetsStore } from '@stores/assets';

export default {
    name: 'DowntimeNotificationCard',
    components: {
        CauseCard,
        CauseTableSelect,
        PlannedShutdownBanner,
        MediaInputManager,
        MediaGridManager
    },
    props: {
        notification: {
            validator(value) {
                return typeof value === 'object' || value === null;
            },
            required: false
        },
        index: {
            type: Number,
            required: false
        }
    },
    async mounted() {
        await this.initialize();
    },
    data() {
        return {
            asset: null,
            proximityReading: null,
            form: {
                cause: null,
                description: ''
            },

            // cause selector
            causeSelectProps: {
                url: '',
                queryParams: { order: 'name' },
                label: this.$t('Assign a cause to this event'),
                dialogTitle: this.$t('Select downtime cause'),
                searchLabel: this.$t('Search cause'),
                selection: null,
                facilityId: null,
                hasCauses: true
            },
            initSuccess: null,
            isLoading: false,
            expand: null,
            justificationMedia: []
        };
    },
    computed: {
        ...mapState(useAssetsStore, {
            getAssetByUUID: 'getAssetByUUID'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale',
            getUser: 'user'
        }),
        /**
         * Get the received date of the notification.
         * @return {String} - Received date or time.
         */
        receivedAt() {
            if (this.isReceivedToday) {
                return getTime(this.notification['receivedAt']);
            }
            return getLocaleFormat(
                this.notification['receivedAt'],
                this.userLocale
            );
        },
        /**
         * Payload to send server.
         * @return {Object}
         */
        payload() {
            const payload = {
                uuid: this.notification['eventUUID'],
                cause_id: null,
                description: this.form.description
            };
            // check if values has been selected
            if (this.form.cause) {
                payload['cause_id'] = this.form.cause.id;
            }

            return payload;
        },
        /**
         * If the notification is new
         * @return {Boolean}
         */
        isNew() {
            return this.notification['isNew'];
        },
        /**
         * Show reload banner.
         * Only show if an attempt to load has been made and there was a failure.
         * @return {Boolean}
         */
        showReloadBanner() {
            if (this.initSuccess === null) {
                return false;
            } else {
                return !this.initSuccess && !this.isLoading;
            }
        },
        /**
         * Allow dismiss notification popup
         */
        allowDismiss() {
            // check if the user is always allowed to dismiss
            if (this.$userCan('always_dismiss', 'downtime_notifications')) {
                return true;
            }
            // if not, check if the asset allows it
            return this.asset ? this.asset.allow_downtime_dismissal : false;
        },
        /**
         * Open expansion based on indes of notification
         * if the notification is at the 0th index then it is at the top of the list
         * and should be open
         */
        triggerExpansion: {
            get() {
                return this.expand === null
                    ? this.index === 0 || this.expand
                    : this.expand;
            },
            set(val) {
                this.expand = val;
                return this.expand;
            }
        },
        /**
         * Controls the PlannedShutdownBanner visibility by checking if
         * the type of loss for the selected cause is planned shutdown
         */
        isPlannedShutdown() {
            if (this.causeSelectProps.selection) {
                return (
                    this.causeSelectProps.selection.type_of_loss === 'shutdown'
                );
            }
            return false;
        },
        /**
         * Check if current asset has causes
         */
        hasCauses() {
            return this.causeSelectProps.hasCauses;
        },
        /**
         * Check if the notification received date is today.
         * @return {Boolean} - True if received today.
         */
        isReceivedToday() {
            return (
                new Date(this.notification['receivedAt']).toDateString() ===
                new Date().toDateString()
            );
        },
        /**
         * Verify the state of the MEDIA_UPLOAD feature flag
         * @return {Boolean}
         */
        isMediaUploadEnabled() {
            return this.isFeatureEnabled('MEDIA_UPLOAD');
        }
    },
    methods: {
        isFeatureEnabled,
        ...mapActions(useNotificationsStore, ['dismissNotificationAction']),
        /**
         * Initialize component.
         */
        async initialize() {
            // check if asset details can be fetched from the store
            this.asset = this.getAssetByUUID(this.notification['assetUUID']);

            this.proximityReading =
                this.notification['proximity_reading'] !== undefined
                    ? this.notification['proximity_reading']
                    : null;

            // if asset is not in store, fetch it from the server
            if (this.asset === null) {
                this.asset = await this.getAsset();
            }

            // initialize component if asset was loaded
            if (this.asset) {
                // initialize url to use for cause selector
                this.causeSelectProps.url = `/assets/${this.asset.id}/causes/`;
                this.causeSelectProps.facilityId = this.asset.facility;
                this.initSuccess = true;
            } else {
                this.initSuccess = false;
            }
        },
        /**
         * Get asset detail from the server if asset data is not loaded in the store.
         * @return {Object} - Asset.
         */
        async getAsset() {
            this.isLoading = true;
            let asset = null;
            try {
                let response = await this.$axios.get(
                    `assets/?uuid=${this.notification['assetUUID']}`
                );
                if (response.status === 200) {
                    if (response.data.results) {
                        asset = response.data.results[0];
                    }
                } else {
                    console.error('Failed to fetch asset due to: ');
                }
            } catch (error) {
                console.error('Failed to fetch asset due to: ', error);
            }
            this.isLoading = false;
            return asset;
        },
        /**
         * Handle submit.
         */
        async submit() {
            this.isLoading = true;
            // After making the API call, the card closes right away,
            // which means that this.$t will not be accessible, resulting in an error being thrown.
            const successMessage = this.$t('Downtime cause saved');
            const errorMessage = this.$t(
                'Failed to submit event. Please try again later'
            );

            try {
                if (this.justificationMedia.length > 0) {
                    const mediaFiles = this.justificationMedia.map(
                        media => media.file
                    );
                    await justifyEventWithMedia(mediaFiles, this.payload);
                } else {
                    await this.$axios.post('events/resolve/', this.payload);
                }

                this.$notifySuccess(successMessage);
            } catch (error) {
                this.$notifyError(errorMessage, error);
            }
            this.isLoading = false;
        },
        /**
         * Dismiss downtime notification
         */
        async dismiss() {
            try {
                this.dismissNotificationAction(this.notification['eventUUID']);
            } catch (error) {
                console.error('Error dismissing notification due to: ', error);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.notification {
    max-width: 100%;
    border-width: 0.1rem;
    border-color: rgba(0, 0, 0, 0.12);
    font-size: 1rem;
}
.notification-header {
    height: 60px;
}
.event-badge {
    font-size: 16px;
    padding: 4px 6px;
    margin-top: 5px;
}
</style>
